import Logo from "../../../../assets/icons/samtree-logo.png"

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="mb-10">
        <h2 className="text-white text-xl font-semibold mb-3 border-b border-gray-700 pb-2">{title}</h2>
        <div className="text-gray-300 text-sm leading-relaxed space-y-2">{children}</div>
    </section>
)

const Privacy = () => {
    return (
        <div className="bg-[#0d0d0d] min-h-screen w-full overflow-x-hidden">
            <header className="flex flex-col items-center justify-center py-12 border-b border-gray-800">
                <img src={Logo} alt="Samtree Logo" className="w-20 h-20 object-contain mb-4" />
                <h1 className="text-white text-4xl font-bold tracking-tight">Privacy Policy</h1>
                <p className="text-gray-400 text-sm mt-2">Samtree &mdash; WearOS Watch Face Applications</p>
                <p className="text-gray-500 text-xs mt-1">Effective Date: March 13, 2026 &nbsp;|&nbsp; Last Updated: March 13, 2026</p>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">

                <Section title="1. Overview">
                    <p>
                        Samtree ("we," "our," or "us") develops and publishes watch face applications for Wear OS by Google
                        using WatchFace Studio. This Privacy Policy explains our practices regarding the access, use,
                        and handling of any data in connection with our applications.
                    </p>
                    <p>
                        We are committed to protecting your privacy. By using our watch face applications, you agree to
                        the terms described in this policy.
                    </p>
                </Section>

                <Section title="2. Data We Access">
                    <p>
                        Our watch face applications are built exclusively with WatchFace Studio and may read the following
                        on-device sensor data solely for the purpose of displaying real-time information on the watch face:
                    </p>
                    <ul className="list-disc list-inside space-y-1 mt-2 text-gray-300">
                        <li>Heart Rate (BPM) &mdash; displayed as a real-time complication on the watch face</li>
                        <li>Step Count &mdash; displayed as a daily step counter complication</li>
                        <li>Battery Level &mdash; displayed as a device status complication</li>
                        <li>Date & Time &mdash; used as the primary function of the watch face</li>
                    </ul>
                    <p className="mt-2">
                        All data listed above is read directly from the Wear OS system and is rendered on-screen in real time.
                        <strong className="text-white"> This data is never transmitted, uploaded, or shared with Samtree or any third party.</strong>
                    </p>
                </Section>

                <Section title="3. Data Collection and Storage">
                    <p>
                        <strong className="text-white">Samtree does not collect, store, or retain any user data.</strong>
                    </p>
                    <p>
                        Our watch face applications do not include any backend server communication, database, analytics SDK,
                        or remote logging system. All sensor readings (including health data) are processed entirely on your
                        Wear OS device by the operating system and WatchFace Studio runtime. No data leaves your device as
                        a result of using our applications.
                    </p>
                    <p>
                        We do not use third-party analytics services (e.g., Firebase Analytics, Google Analytics) or
                        advertising SDKs within our watch face applications.
                    </p>
                </Section>

                <Section title="4. Data Retention">
                    <p>
                        Because Samtree does not collect or store any user data, there is no retention period applicable
                        to our applications. All health and sensor data remains solely on your device and is managed
                        by the Wear OS operating system according to Google's platform policies.
                    </p>
                    <p>
                        If you wish to manage or delete health data on your device, please refer to the settings of
                        your Wear OS device or the Google Health Connect application.
                    </p>
                </Section>

                <Section title="5. Data Deletion">
                    <p>
                        Since Samtree does not collect or store any personal data, there is no user data held by us
                        that can be requested for deletion.
                    </p>
                    <p>
                        To remove all data associated with the watch face application from your device, simply uninstall
                        the application from your Wear OS device. Uninstalling the application will remove all local
                        application data managed by the Wear OS system.
                    </p>
                    <p>
                        If you have questions about data stored on your device by the Wear OS platform or Google Health
                        Connect, please contact Google directly or refer to
                        &nbsp;<a href="https://support.google.com/android" className="text-blue-400 underline" target="_blank" rel="noreferrer">Google's support page</a>.
                    </p>
                </Section>

                <Section title="6. Health Data">
                    <p>
                        Our applications may access Health Data (such as heart rate) through the Wear OS sensor framework
                        solely to display this information visually on the watch face. This access is read-only and
                        occurs entirely on-device. Samtree does not process, transmit, sell, or share Health Data in
                        any form.
                    </p>
                    <p>
                        Health Data is not used for advertising, profiling, or any purpose other than real-time display
                        on the watch face as explicitly requested by the user through their chosen watch face configuration.
                    </p>
                </Section>

                <Section title="7. Children's Privacy">
                    <p>
                        Our applications do not knowingly collect any information from children under the age of 13.
                        Since we collect no user data whatsoever, our applications are safe for use by all age groups
                        with no privacy risk from data collection.
                    </p>
                </Section>

                <Section title="8. Changes to This Policy">
                    <p>
                        We may update this Privacy Policy from time to time. Any changes will be reflected on this page
                        with an updated effective date. We encourage you to review this policy periodically.
                        Continued use of our applications after any modifications constitutes your acknowledgment
                        of the updated policy.
                    </p>
                </Section>

                <Section title="9. Contact Us">
                    <p>
                        If you have any questions or concerns about this Privacy Policy or our data practices, please
                        contact us at:
                    </p>
                    <p className="mt-2">
                        <strong className="text-white">Samtree</strong><br />
                        Email: <a href="mailto:help@samtree.com" className="text-blue-400 underline">help@samtree.com</a><br />
                        Website: <a href="https://isamtree.com" className="text-blue-400 underline" target="_blank" rel="noreferrer">https://isamtree.com</a>
                    </p>
                </Section>

            </main>

            <footer className="border-t border-gray-800 py-6 text-center text-gray-600 text-xs">
                &copy; {new Date().getFullYear()} Samtree. All rights reserved.
            </footer>
        </div>
    )
}

export default Privacy;